import { SectionCard, formatDate } from '@datahub/design-system'
import type { StoredPortfolioCompany } from './StearsPortfolioCard'
import { withCurrency } from '../ticket-size'

export interface CoreFundProfileSnapshot {
  slug: string
  kind: 'gp' | 'lp'
  name: string
  description: string | null
  foundingYear: number | null
  websiteUrl: string | null
  address: string | null
  currency: string | null
  assetsUnderManagement: number | null
  totalCommitted: number | null
  investorStatus: string | null
  hqCityCountry: string | null
  legalStructure: string | null
  ticketType: string | null
  ticketSizeCurrency: string | null
  impactReports: string | null
  reports: string | null
  investmentMandate: string | null
  strategyDescription: string | null
  locationCriteria: string | null
  environmentalCriteria: string | null
  socialCriteria: string | null
  governanceCriteria: string | null
  financialAndOtherCriteria: string | null
  formerNames: string[]
  investorTypes: string[]
  regionsOfInvestment: string[]
  hqRegions: string[]
  countriesOfInvestment: string[]
  sectorPreferences: string[]
  investmentStages: string[]
  stagePreferences: string[]
  thematicFocuses: string[]
  preferredFundTypes: string[]
  investmentStrategies: string[]
  instruments: string[]
  sdgFocuses: string[]
  ticketSizes: string[]
  funds: { name: string; vintageYear: number | null }[]
  commitments: string[]
  /** Laid out by `StearsPortfolioCard`; older snapshots hold bare names. */
  portfolioCompanies: StoredPortfolioCompany[]
}

function Rows({ entries }: { entries: [string, string][] }) {
  return (
    <div className="flex flex-col">
      {entries.map(([label, value]) => (
        <div key={label} className="flex items-start" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
          <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
          <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1, wordBreak: 'break-word' }}>{value}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * What Stears Core holds about a fund that the Datahub dictionary has no field for: the
 * vehicles and commitments Core lists as relationships, and the free-typed ticket sizes
 * that match none of the dictionary's bands. Portfolio companies have their own card
 * above — there are too many of them to read as one comma-joined row.
 *
 * Everything else Core knows seeds the fund profile above and appears in the same fields
 * the fund will edit, marked "Stears" until it answers — Core is a seed for that one
 * profile, not a second record beside it. So this card shows nothing the record rows
 * already show: no head office, and only the countries of investment that are not in the
 * record's countries of operation (F5). Each ticket size shows with its currency (F6).
 */
export function StearsFundCard({
  profile,
  fetchedAt,
  shownCountries = [],
  ticketSizeCurrency = null,
}: {
  profile: CoreFundProfileSnapshot
  fetchedAt: string | null
  /** The country names the record rows already show. */
  shownCountries?: string[]
  /** The entity's ticket size currency, used when the Stears record has none. */
  ticketSizeCurrency?: string | null
}) {
  const joined = (list: string[]): string | null => (list.length > 0 ? list.join(', ') : null)
  const shown = new Set(shownCountries.map((c) => c.trim().toLowerCase()))

  const rows: [string, string | null][] = [
    [
      'Funds Stears lists',
      profile.funds.length > 0
        ? profile.funds.map((f) => (f.vintageYear ? `${f.name} (${f.vintageYear})` : f.name)).join(', ')
        : null,
    ],
    ['Commitments Stears lists', joined(profile.commitments)],
    [
      'Ticket sizes as Stears records them',
      joined(withCurrency(profile.ticketSizes, profile.ticketSizeCurrency ?? ticketSizeCurrency)),
    ],
    ['Instruments', joined(profile.instruments)],
    ['Thematic focus', joined(profile.thematicFocuses)],
    ['Preferred fund types', joined(profile.preferredFundTypes)],
    ['Countries of investment', joined((profile.countriesOfInvestment ?? []).filter((c) => !shown.has(c.trim().toLowerCase())))],
    ['Former names', joined(profile.formerNames)],
    ['Strategy note', profile.strategyDescription],
  ]
  const entries = rows.filter((r): r is [string, string] => Boolean(r[1]))
  if (entries.length === 0) return null

  return (
    <SectionCard title="Also on Stears, with no Datahub field">
      <div className="flex flex-col">
        <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '15px', padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
          Context only{fetchedAt ? `, pulled ${formatDate(fetchedAt)}` : ''}. The rest of what Stears holds seeds the
          profile above and is shown there, in the fields the fund edits.
        </p>
        <Rows entries={entries} />
      </div>
    </SectionCard>
  )
}
